import React, { useState, useCallback, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { Info } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import Table from '../../../components/table/Table';
import {
  useGetProcessTypeLookupQuery,
  useGetMakerCheckerStatusLookupQuery,
  useGetIbanByIdQuery,
} from '../services/approvalService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  MakerCheckerItem,
  FastOutgoingMessagesDto,
  SendMoneyListDto,
  WithdrawalDto,
  Iban,
} from '../../../types/approval.types';
import { typeToDescription } from '../../../utils/approvalUtils';
import FastTransactionDetail from '../components/FastTransactionDetail';
import WithdrawalTransactionDetail from '../components/WithdrawalTransactionDetail';
import SendMoneyTransactionDetail from '../components/SendMoneyTransactionDetail';
import { useFetchDataQuery } from '../../../components/services/services';
import { filterOperatorsDateTime } from '../../../utils/filter-date-time';
import BulkSendMoneyTransactionDetail from '../components/BulkSendMoneyTransactionDetail';
import { usePermission } from '../../../hooks/usePermission';

interface SentForApprovalListProps {
  onRefresh?: () => void;
}

const SentForApprovalList: React.FC<SentForApprovalListProps> = ({ onRefresh }) => {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  
  // Permission kontrolü
  const { hasPermission: canViewApprovedRejectedList, isLoading: isPermissionLoading } = usePermission({
    code: 'Corporate.POST.Reading.Onay/RedListesineErişim'
  });
  
  // Modal states
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MakerCheckerItem | null>(null);

  // Transaction data states
  const [fastData, setFastData] = useState<FastOutgoingMessagesDto | null>(null);
  const [sendMoneyData, setSendMoneyData] = useState<SendMoneyListDto | null>(null);
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalDto | null>(null);
  const [ibanData, setIbanData] = useState<Iban | null>(null);

  // API queries
  const { data: processTypesResponse } = useGetProcessTypeLookupQuery();
  const { data: statusTypesResponse } = useGetMakerCheckerStatusLookupQuery();
  
  const processTypes = Array.isArray((processTypesResponse as any)?.data) ? (processTypesResponse as any).data : [];
  const statusTypes = Array.isArray(statusTypesResponse?.data) ? statusTypesResponse.data : [];

  const [bulkSendMoneyListWithWalletNoDto, setBulkSendMoneyListWithWalletNoDto] = useState<any>(null);
  const [bulkSendMoneyListWithPhoneNoDto, setBulkSendMoneyListWithPhoneNoDto] = useState<any>(null);
  const [bulkSendMealAllowanceListWithWalletNoDto, setBulkSendMealAllowanceListWithWalletNoDto] = useState<any>(null);
  const [bulkSendMealAllowanceListWithCardNoDto, setBulkSendMealAllowanceListWithCardNoDto] = useState<any>(null);
  const [bulkSendCashbackListDto, setBulkSendCashbackListDto] = useState<any>(null);

  const currentUserState = useSelector((state: RootState) => state.currentUserState.currentUserState);
  const listUrl = `/MakerChecker/GetMakerCheckersViewListV2?customerId=${currentUserState?.companyId}`;
  const [requestBody, setRequestBody] = useState({
    filters: [],
    pageSize: 25,
    page: 1,
    orderBy: '',
    orderDesc: false,
    customerId: currentUserState?.companyId || undefined,
  });
  const {
    data: fetchData,
    isFetching,
    isLoading: isFetchLoading,
    refetch,
  } = useFetchDataQuery({ url: listUrl, filter: requestBody, method: 'POST' }, { 
    skip: !canViewApprovedRejectedList || !requestBody?.customerId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true
  });

  // Component mount olduğunda veya customerId değiştiğinde refetch yap
  React.useEffect(() => {
    if (canViewApprovedRejectedList && requestBody?.customerId) {
      refetch();
    }
  }, [canViewApprovedRejectedList, requestBody?.customerId, refetch]);

  // Tab'e geri dönüldüğünde refetch yap
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && canViewApprovedRejectedList && requestBody?.customerId) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [canViewApprovedRejectedList, requestBody?.customerId, refetch]);

  // Get IBAN data when needed
  const { data: ibanResponse } = useGetIbanByIdQuery(withdrawalData?.Data?.IbanId || 0, {
    skip: !withdrawalData?.Data?.IbanId,
  });

  React.useEffect(() => {
    if (ibanResponse?.data) {
      setIbanData(ibanResponse.data);
    }
  }, [ibanResponse]);

  const createModelForType = (json: string) => {
    try {
      return JSON.parse(json);
    } catch (error) {
      return null;
    }
  };

  const createModel = useCallback((json: string, type: string) => {
    try {
      const parsedData = createModelForType(json);
      switch (type) {
        case 'WithdrawalDto':
          setWithdrawalData(parsedData);
          break;
        case 'SendMoneyListDto':
          setSendMoneyData(parsedData);
          break;
        case 'FastOutgoingMessagesDto':
          setFastData(parsedData);
          break;
        case 'BulkSendMoneyListWithWalletNoDto':
          setBulkSendMoneyListWithWalletNoDto(parsedData);
          break;
        case 'BulkSendMoneyListWithPhoneNoDto':
          setBulkSendMoneyListWithPhoneNoDto(parsedData);
          break;
        case 'BulkSendMealAllowanceListWithWalletNoDto':
          setBulkSendMealAllowanceListWithWalletNoDto(parsedData);
          break;
        case 'BulkSendMealAllowanceListWithCardNoDto':
          setBulkSendMealAllowanceListWithCardNoDto(parsedData);
          break;
        case 'BulkSendCashbackListDto':
          setBulkSendCashbackListDto(parsedData);
          break;
        default:
          break;
      }
    } catch (error) {
      return null;
    }
  }, []);

  const handleDetail = useCallback(
    (item: MakerCheckerItem) => {
      setSelectedItem(item);
      createModel(item.jsonNewValue, item.reflectionType);
      setIsDetailModalOpen(true);
    },
    [createModel]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createDate',
        headerName: 'İşlem Tarihi',
        width: 180,
        type: 'datetime',
        filterOperators: filterOperatorsDateTime,
      },
      {
        field: 'type',
        headerName: 'İşlem Türü',
        width: 150,
        type: 'singleSelect',
        valueOptions: processTypes.map((pt: any) => ({
          value: pt.value,
          label: pt.text
        })),
        renderCell: (params: any) => {
          const processType = processTypes.find((pt: any) => pt.value === params.value);
          return processType ? processType.text : params.value;
        },
      },
      {
        field: 'status',
        headerName: 'Durumu',
        width: 120,
        type: 'singleSelect',
        valueOptions: statusTypes.map((st: any) => ({
          value: st.value,
          label: st.text
        })),
        renderCell: (params: any) => {
          const statusType = statusTypes.find((st: any) => st.value === params.value);
          return statusType ? statusType.text : params.value;
        },
      },
      {
        field: 'makerName',
        headerName: 'İşlemi Yapan Kullanıcı',
        width: 200,
      },
      {
        field: 'actions',
        headerName: 'İşlem Detayı',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const item = params.row as MakerCheckerItem;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Info />}
                onClick={() => handleDetail(item)}
                sx={{
                  borderColor: '#2196f3',
                  color: '#2196f3',
                  minWidth: '85px',
                  fontSize: '14px',
                  py: 0.5,
                  '&:hover': {
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    bgcolor: 'rgba(33, 150, 243, 0.04)',
                  },
                }}
              >
                Detay
              </Button>
            </Box>
          );
        },
      },
    ] as GridColDef[],
    [processTypes, statusTypes, handleDetail]
  );

  const renderTransactionDetail = () => {
    if (!selectedItem) return null;

    switch (selectedItem.reflectionType) {
      case 'FastOutgoingMessagesDto':
        return fastData ? <FastTransactionDetail data={fastData} /> : null;
      case 'WithdrawalDto':
        return withdrawalData && ibanData ? (
          <WithdrawalTransactionDetail data={withdrawalData} iban={ibanData} />
        ) : null;
      case 'SendMoneyListDto':
        return sendMoneyData ? <SendMoneyTransactionDetail data={sendMoneyData} /> : null;
      case 'BulkSendMoneyListWithWalletNoDto':
        return bulkSendMoneyListWithWalletNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMoneyListWithWalletNoDto} />
        ) : null;
      case 'BulkSendMoneyListWithPhoneNoDto':
        return bulkSendMoneyListWithPhoneNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMoneyListWithPhoneNoDto} />
        ) : null;
      case 'BulkSendMealAllowanceListWithWalletNoDto':
        return bulkSendMealAllowanceListWithWalletNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMealAllowanceListWithWalletNoDto} />
        ) : null;
      case 'BulkSendMealAllowanceListWithCardNoDto':
        return bulkSendMealAllowanceListWithCardNoDto ? (
          <BulkSendMoneyTransactionDetail data={bulkSendMealAllowanceListWithCardNoDto} />
        ) : null;
      case 'BulkSendCashbackListDto':
        return bulkSendCashbackListDto ? <BulkSendMoneyTransactionDetail data={bulkSendCashbackListDto} /> : null;

      default:
        return null;
    }
  };

  const debounce = (func: any, delay: any) => {
    let timeoutId: any;
    return (...args: any) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const onFilterChange = debounce((filterObj: any) => {
    setRequestBody((prev) => ({ ...prev, filters: filterObj }));
  }, 500);

  const handleChangePagination = (model: any) => {
    setPaginationModel(model);
    setRequestBody((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page + 1 }));
  };

  const sortModelChange = (model: any) => {
    setRequestBody((prev) => ({ ...prev, orderBy: model[0]?.field ?? '', orderDesc: model[0]?.sort === 'asc' }));
  };

  return (
    <Box>
      <Table
        sx={{
          margin: fetchData?.data?.length > 0 && paginationModel.pageSize > 25 ? '0px 0 25px 0 !important' : '0px !important',
        }}
        columnVisibilityConfigKey="sent-approval-list"
        filterKey="sent-approval-filter"
        sortModelChange={sortModelChange}
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        checkboxSelection
        isMasterDetailActive={false}
        headerFilters={true}
        rows={fetchData?.data ?? []}
        columns={columns}
        isLoading={isPermissionLoading || isFetching || (fetchData === undefined && !!canViewApprovedRejectedList && !!requestBody?.customerId)}
        handleFilterChange={onFilterChange}
        paginationModel={paginationModel}
        onPaginationModelChange={handleChangePagination}
        pageSizeOptions={[25, 50, 100]}
        rowCount={fetchData?.totalCount ?? 0}
        getRowId={(row: any) => row.id}
        idName={'id'}
        listUrl={listUrl}
        fetchData={fetchData}
        customerId={currentUserState?.companyId || undefined}
        showDownloadButton={true}
        listName="Onay İçin Gönderilen İşlemler Listesi"
        requestBody={requestBody}
        fetchMethod="POST"
      />

      {/* Detail Modal */}
      <ConfirmationModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={selectedItem ? `${typeToDescription(selectedItem)} İşlem Detayı` : 'İşlem Detayı'}
        confirmButtonText=""
        closeButtonText=""
        isLoading={false}
        optionalWidth="800px"
        customHeaderStyle={{
          background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
          titleColor: 'white',
          icon: <Info sx={{ color: 'white' }} />,
        }}
      >
        {renderTransactionDetail()}
      </ConfirmationModal>
    </Box>
  );
};

export default SentForApprovalList;
